import React, { useRef, useState } from 'react';
import Button from '@components/ui-old/button';
import Counter from '@components/common/counter';
import { useRouter } from 'next/router';
import { useProductDetailsQuery } from '@framework/product/get-product-details';
import { getVariations } from '@framework/utils/get-variations';
import { useCart } from '@contexts/cart/cart.context';
import { generateCartItem } from '@utils/generate-cart-item';
import isEmpty from 'lodash/isEmpty';
import Link from '@components/ui-old/link';
import { useWindowSize } from '@utils/use-window-size';
import { Swiper, SwiperSlide } from 'swiper/react';
import ProductMetaReview from '@components/product/product-meta-review';
import { useSsrCompatible } from '@utils/use-ssr-compatible';
import { Product, Category } from '@framework/types';
import { useCategoriesQuery } from '@framework/category/get-all-categories';
import { useTranslation } from 'next-i18next';
import ProductSingleDetailsSkeleton from '../ui-old/loaders/ProductSingleDetailsSkeleton';
import { useUI } from '@contexts/ui.context';
import Image from 'next/image';
import { getThemedImage } from '@components/layout/get-themed-image';
import { siteSettings } from '@settings/site-settings';
import { NextSeo, ProductJsonLd, BreadcrumbJsonLd } from 'next-seo';
import ProductNotFound from './product-not-found';
import { Lens } from '@components/ui/lens';
import { AnimatePresence } from 'framer-motion';
import ImageModal from '@components/other/product-image-modal';
import { ROUTES } from '@utils/routes';
import GlobalShare from '@components/other/global-share';
import { getCurrentLocationAndLanguage } from '@utils/location';
import { Progress } from '@components/ui/progress-shop';
import { useProgressTimer } from '@/hooks/use-progress-timer';
import { ToastProvider, Toast, ToastTitle, ToastClose, ToastViewport } from '@/components/ui/toast';
import { CircleCheckIcon, XIcon } from 'lucide-react';
interface Limit {
  value: number | null;
  used: number | null;
  limit: any | null;
}
interface Limits {
  monthly?: Limit;
  daily?: Limit;
  once?: Limit;
}
const getMaxLimit = (lim: any): number | null => {
  if (!lim) return null;
  if (typeof lim === 'object' && !Array.isArray(lim)) {
    return lim?.daily?.value ?? lim?.monthly?.value ?? lim?.once?.value ?? null;
  }
  if (Array.isArray(lim)) {
    const map: Record<string, any> = {};
    lim.forEach((l: any) => {
      if (l?.limit_type) map[l.limit_type] = l;
    });
    return map.daily?.value ?? map.monthly?.value ?? map.once?.value ?? null;
  }
  return null;
};
const productGalleryCarouselResponsive = {
  '1024': {
    slidesPerView: 1
  },
  '768': {
    slidesPerView: 1
  },
  '0': {
    slidesPerView: 1
  }
};
const ProductSingleDetails: React.FC = () => {
  const {
    t
  } = useTranslation('common');
  const {
    location
  } = getCurrentLocationAndLanguage();
  const {
    query
  } = useRouter();
  const slug = query.slug as string;
  const id = slug.split('-')[0];
  const {
    width
  } = useSsrCompatible(useWindowSize(), {
    width: 0,
    height: 0
  });
  const {
    data: product,
    isLoading
  } = useProductDetailsQuery(id);
  const {
    data: categoriesData,
    isLoading: isCategoriesLoading
  } = useCategoriesQuery({});
  const {
    addItemToCart,
    items: cartItems
  } = useCart();
  const [attributes] = useState<{
    [key: string]: string;
  }>({});
  const [quantity, setQuantity] = useState(1);
  const [addToCartLoader, setAddToCartLoader] = useState<boolean>(false);
  const {
    isAuthorized,
    openModal,
    setModalView
  } = useUI();
  const [isDescriptionExpanded, setIsDescriptionExpanded] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [isLensActive, setIsLensActive] = useState(false);
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const swiperRef = useRef<any>(null);
  const [toastOpen, setToastOpen] = useState(false);
  const [toastMessage, setToastMessage] = useState('');
  const [toastVariant, setToastVariant] = useState<'default' | 'destructive'>('default');
  const toastDuration = 3000;
  const {
    progress,
    start,
    pause,
    resume
  } = useProgressTimer({
    duration: toastDuration,
    onComplete: () => setToastOpen(false)
  });
  if (isLoading || isCategoriesLoading) return <ProductSingleDetailsSkeleton />;
  if (!product) return <ProductNotFound />;
  const {
    name,
    description,
    shortDescription,
    cc,
    productCode,
    strPrice,
    photo,
    category,
    variations: productVariations = {},
    collection = [],
    limits,
    amount,
    expirationDate,
    certificate,
    usage,
    ingredients,
    catalog,
    weight
  } = product || {};
  const categoryId = typeof category === 'object' && category !== null ? category.id : category;
  const categoryName = categoriesData?.categories.data.find((cat: Category) => cat.id === categoryId)?.name;
  const variations = getVariations(productVariations);
  const isSelected = !isEmpty(variations) ? !isEmpty(attributes) && Object.keys(variations).every(variation => attributes.hasOwnProperty(variation)) : true;
  function addToCart() {
    if (!product) return;
    if (location !== 'kg' && !isAuthorized) {
      setModalView('LOGIN_VIEW');
      openModal();
      return;
    }
    if (!isSelected) return;
    const existingItem = Array.isArray(cartItems) ? cartItems.find((ci: any) => ci.id === product!.id) : null;
    const existingQty = existingItem ? existingItem.quantity || 0 : 0;
    const maxLimit = getMaxLimit(limits);
    if (maxLimit && existingQty + quantity > maxLimit) {
      setToastMessage(t('text-limit-reached'));
      setToastVariant('destructive');
      setToastOpen(true);
      start();
      return;
    }
    setAddToCartLoader(true);
    setTimeout(() => {
      setAddToCartLoader(false);
    }, 600);
    const item = generateCartItem(product as Product, attributes);
    addItemToCart(item, quantity);
    setToastMessage(t('text-added-to-cart'));
    setToastVariant('default');
    setToastOpen(true);
    start();
  }
  const renderLimits = () => {
    if (!limits) return null;
    const limitTypes: (keyof Limits)[] = ['monthly', 'daily'];
    return <div className="flex flex-nowrap md:flex-wrap gap-4" data-sentry-component="renderLimits" data-sentry-source-file="product-single-details.tsx">
        {limitTypes.map(type => {
        const limit = (limits as Limits)[type];
        if (limit && limit.value !== null) {
          return <div key={type} className="bg-aloeGold min-w-[12rem] p-4 rounded-xl shadow-xl text-center flex flex-col items-center shrink-0 md:shrink">
                <span className="bg-red-500 text-white text-xs px-2 py-1 rounded-xl mb-2">
                  {t(`limit-${type}`)}
                </span>
                <p className="text-sm text-neutral-900 font-medium mb-1">
                  {t(`limit-${type}-value`, {
                value: limit.value
              })}
                </p>
                <p className="text-sm text-neutral-900 font-medium">
                  {t(`limit-${type}-used`, {
                used: limit.used || 0
              })}
                </p>
              </div>;
        }
        return null;
      })}
      </div>;
  };
  const handleMouseEnter = () => {
    timerRef.current = setTimeout(() => {
      setIsLensActive(true);
    }, 1000); // 1 second later activate Lens
  };
  const handleMouseLeave = () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
    }
    setIsLensActive(false);
  };
  const stripHtml = (html: string) => {
    if (!html) return '';
    if (typeof window === 'undefined') return '';
    const tmp = document.createElement('DIV');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };
  const getTruncatedDescription = (html: string, limit: number) => {
    const text = stripHtml(html);
    if (text.length <= limit) return html;
    const truncatedText = text.slice(0, limit) + '';
    return truncatedText;
  };
  const productUrl = `${siteSettings.author.websiteUrl}e-shop/${slug}`;
  const galleryImages = [photo?.x600 ?? '/assets/placeholder/products/product-cat.svg'];
  return <ToastProvider swipeDirection="right" data-sentry-element="ToastProvider" data-sentry-component="ProductSingleDetails" data-sentry-source-file="product-single-details.tsx">
      <NextSeo title={`${name || 'Product'} - ${strPrice || 'Product'} | ${siteSettings.name || 'Forever Living Product'}`} description={`${shortDescription || 'Product'}`} canonical={productUrl} openGraph={{
      url: productUrl,
      title: `${name || 'Product'} - ${strPrice || 'Product'} | ${siteSettings.name || 'Forever Living Product'}`,
      description: `${shortDescription || 'Product'}`,
      images: photo && photo.x600 ? [{
        url: photo.x600,
        width: 800,
        height: 600,
        alt: `${name || 'Product'}`
      }] : [{
        url: `${siteSettings.author.websiteUrl}assets/images/og-image-01.png`,
        width: 1200,
        height: 630,
        alt: `${name || 'Product'}`
      }],
      site_name: siteSettings.name || 'Forever Living Product'
    }} twitter={{
      handle: '@handle',
      site: '@site',
      cardType: 'summary_large_image'
    }} data-sentry-element="NextSeo" data-sentry-source-file="product-single-details.tsx" />
      <ProductJsonLd productName={name} images={[photo?.x600 || `${siteSettings.author.websiteUrl}assets/images/og-image-01.png`]} description={stripHtml(shortDescription || description || '')} sku={productCode} brand="Forever Living" offers={{
      price: product?.price?.toString() || '0',
      priceCurrency: product?.currency || '',
      url: productUrl,
      itemCondition: 'https://schema.org/NewCondition',
      availability: amount === 0 ? 'https://schema.org/OutOfStock' : 'https://schema.org/InStock'
    }} data-sentry-element="ProductJsonLd" data-sentry-source-file="product-single-details.tsx" />
      <BreadcrumbJsonLd itemListElements={[{
      position: 1,
      name: t('text-home'),
      item: `${siteSettings.author.websiteUrl}`
    }, {
      position: 2,
      name: t('text-shop'),
      item: `${siteSettings.author.websiteUrl}e-shop/`
    }, {
      position: 3,
      name: name || 'FLP',
      item: productUrl
    }]} data-sentry-element="BreadcrumbJsonLd" data-sentry-source-file="product-single-details.tsx" />
      <div className="block lg:grid grid-cols-9 pt-7 pb-10 lg:pb-14 2xl:pb-20 items-start px-4 md:px-6 lg:px-8 max-w-8xl mx-auto">
        {width < 1025 ? <>
            <Swiper breakpoints={productGalleryCarouselResponsive} onSwiper={swiper => swiperRef.current = swiper} onSlideChange={swiper => setActiveImageIndex(swiper.activeIndex)} slidesPerView={1}>
              {galleryImages.map((img, idx) => <SwiperSlide key={`product-gallery-${idx}`}>
                  <div className="transition duration-150 ease-in hover:opacity-90 cursor-pointer rounded-3xl" onClick={() => setIsImageModalOpen(true)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                    {isLensActive ? <Lens zoomFactor={2} lensSize={100} isStatic={false} ariaLabel="Zoom Area">
                        {img === '/assets/placeholder/products/product-cat.svg' ? getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', `${name} ${idx + 1}`, {
                  width: 600,
                  height: 600,
                  className: 'w-full rounded-xl'
                }) : <Image src={img} alt={`${name} ${idx + 1}`} width={600} height={600} className="object-cover w-full rounded-xl" />}
                      </Lens> : <>
                        {img === '/assets/placeholder/products/product-cat.svg' ? getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', `${name} ${idx + 1}`, {
                  width: 600,
                  height: 600,
                  className: 'w-full rounded-xl'
                }) : <Image src={img} alt={`${name} ${idx + 1}`} width={600} height={600} className="object-cover w-full rounded-xl" />}
                      </>}
                  </div>
                </SwiperSlide>)}
            </Swiper>
            {galleryImages.length > 1 && <div className="mt-4 flex gap-2 justify-center">
                {galleryImages.map((img, idx) => <div key={idx} className={`cursor-pointer border rounded-xl overflow-hidden p-1 ${activeImageIndex === idx ? 'border-aloeGold' : 'border-neutral-200 dark:border-neutral-800'}`} onClick={() => {
            setActiveImageIndex(idx);
            if (swiperRef.current) {
              swiperRef.current.slideTo(idx);
            }
          }}>
                    {img === '/assets/placeholder/products/product-cat.svg' ? getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', `${name} thumbnail ${idx + 1}`, {
              width: 60,
              height: 60
            }) : <Image src={img} alt={`${name} thumbnail ${idx + 1}`} width={60} height={60} className="object-cover" />}
                  </div>)}
              </div>}
          </> : <div className="col-span-5 grid grid-cols-1 gap-2.5 2xl:max-w-[600px] 2xl:mr-[250px] xl:max-w-[550px] xl:mr-[130px] lg:w-full lg:mr-[100px] mx-auto border border-neutral-200 dark:border-neutral-800 rounded-3xl shadow-md p-2">
            <div className="transition duration-150 ease-in hover:opacity-90">
              <Swiper breakpoints={productGalleryCarouselResponsive} onSwiper={swiper => swiperRef.current = swiper} onSlideChange={swiper => setActiveImageIndex(swiper.activeIndex)} slidesPerView={1}>
                {galleryImages.map((img, idx) => <SwiperSlide key={`product-gallery-${idx}`}>
                    <div className="transition duration-150 ease-in hover:opacity-90 cursor-pointer rounded-3xl" onClick={() => setIsImageModalOpen(true)} onMouseEnter={handleMouseEnter} onMouseLeave={handleMouseLeave}>
                      {isLensActive ? <Lens zoomFactor={2} lensSize={100} isStatic={false} ariaLabel="Zoom Area">
                          {img === '/assets/placeholder/products/product-cat.svg' ? getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', `${name} ${idx + 1}`, {
                    width: 600,
                    height: 600,
                    className: 'w-full rounded-xl'
                  }) : <Image src={img} alt={`${name} ${idx + 1}`} width={600} height={600} className="object-cover w-full rounded-xl" />}
                        </Lens> : <>
                          {img === '/assets/placeholder/products/product-cat.svg' ? getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', `${name} ${idx + 1}`, {
                    width: 600,
                    height: 600,
                    className: 'w-full rounded-xl'
                  }) : <Image src={img} alt={`${name} ${idx + 1}`} width={600} height={600} className="object-cover w-full rounded-xl" />}
                        </>}
                    </div>
                  </SwiperSlide>)}
              </Swiper>
            </div>
            {galleryImages.length > 1 && <div className="mt-2 flex gap-2 justify-center">
                {galleryImages.map((img, idx) => <div key={idx} className={`cursor-pointer border rounded-xl overflow-hidden p-1 ${activeImageIndex === idx ? 'border-aloeGold' : 'border-neutral-200'}`} onClick={() => {
            setActiveImageIndex(idx);
            if (swiperRef.current) {
              swiperRef.current.slideTo(idx);
            }
          }}>
                    {img === '/assets/placeholder/products/product-cat.svg' ? getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', `${name} thumbnail ${idx + 1}`, {
              width: 60,
              height: 60
            }) : <Image src={img} alt={`${name} thumbnail ${idx + 1}`} width={60} height={60} className="object-cover" />}
                  </div>)}
              </div>}
          </div>}
        <div className="col-span-4 pt-8 lg:pt-0">
          {categoryName ? <Link href={`${ROUTES.SHOP}?category=${categoryId}`} className="text-sm font-medium text-amber-600 dark:text-amber-400 hover:text-amber-700 dark:hover:text-amber-300 transition-colors">
              {categoryName}
            </Link> : null}
          <div className="pb-7 border-neutral-300">
            <h2 className="text-heading text-lg md:text-xl lg:text-2xl 2xl:text-3xl font-bold hover:text-black dark:text-neutral-200 mb-3.5">
              {name}
            </h2>
            <div className="flex flex-row items-center mt-2 space-x-4">
              <div className="text-heading dark:text-neutral-200 font-bold text-3xl md:text-3xl lg:text-4xl 2xl:text-4xl">
                {strPrice}
              </div>
              <span className="bg-black dark:bg-neutral-900 text-white px-2 py-1 flex items-center justify-center rounded-xl text-xs">
                CC {cc}
              </span>
              <span className="bg-black dark:bg-neutral-900 text-white px-3 py-1 flex items-center justify-center rounded-xl text-xs">
                #{productCode}
              </span>
            </div>
          </div>
          {(description || collection && collection.length > 0 || limits) && <div className="border-neutral-300">
              <hr className="my-4 border-t border-neutral-200 dark:border-neutral-800" />
              {description && <div className="text-body text-l lg:text-base leading-6 lg:leading-8 mb-4 text-neutral-900 dark:text-neutral-200">
                  <span dangerouslySetInnerHTML={{
              __html: (isDescriptionExpanded ? description : getTruncatedDescription(description, 500)).replace(/(?:\r\n|\r|\n)/g, '<br/>')
            }}></span>
                  {stripHtml(description).length > 500 && <button onClick={() => {
              const newState = !isDescriptionExpanded;
              setIsDescriptionExpanded(newState);
              if (!newState) {
                window.scrollTo({
                  top: 0,
                  behavior: 'smooth'
                });
              }
            }} className="text-neutral-900 dark:text-neutral-200 hover:underline ml-2">
                      {isDescriptionExpanded ? `- ${t('text-hide')}` : `...${t('text-read-more')}`}
                    </button>}
                </div>}
              {expirationDate && <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {t('text-expiration-date')}: {expirationDate}
                </p>}
              {weight != null && weight > 0 && <p className="text-sm text-neutral-600 dark:text-neutral-300">
                  {t('text-product-weight')}: {(weight / 1000).toFixed(2)} kg
                </p>}
              {certificate && <button onClick={() => window.open(certificate, '_blank')} className="mt-4 px-4 py-2 bg-neutral-900 text-white rounded hover:bg-neutral-600 transition-colors">
                  {t('text-download-certificate') || 'Download Certificate'}
                </button>}
              {catalog && <button onClick={() => window.open(catalog, '_blank')} className="mt-4 px-4 py-2 bg-neutral-900 text-white rounded hover:bg-neutral-600 transition-colors">
                  {t('text-download-catalog') || 'Download Catalog'}
                </button>}
              {usage && <div className="mt-4">
                  <div className="text-body text-l dark:text-neutral-300 lg:text-base leading-6 lg:leading-8" dangerouslySetInnerHTML={{
              __html: usage
            }}></div>
                </div>}
              {ingredients && <div className="mt-4">
                  <div className="text-body text-l dark:text-neutral-300 lg:text-base leading-6 lg:leading-8" dangerouslySetInnerHTML={{
              __html: ingredients
            }}></div>
                </div>}
              {collection && collection.length > 0 && <div className="py-6">
                  <h3 className="text-xl font-semibold mb-4">
                    {t('text-collection')}:
                  </h3>
                  <div className="space-y-4">
                    {collection.map(colItem => <Link key={colItem.item.id} href={`/e-shop/${colItem.item.id}-${colItem.item.name.replace(/\s+/g, '-').toLowerCase()}`} className="flex items-center justify-between border rounded-lg p-4 hover:shadow-lg transition-shadow duration-200">
                        <div className="flex items-center">
                          {colItem.item.photo?.x600 ? <Image src={colItem.item.photo.x600} alt={colItem.item.name} width={80} height={80} className="object-cover w-20 h-20 rounded-md flex-shrink-0" /> : getThemedImage('/assets/placeholder/products/product-cat.svg', '/assets/placeholder/products/product-cat-dark.svg', colItem.item.name, {
                    width: 80,
                    height: 80,
                    className: 'w-20 h-20 rounded-md flex-shrink-0'
                  })}
                          <div className="ml-4">
                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mb-0.5">
                              #{colItem.item.productCode}
                            </p>
                            <h4 className="text-lg font-medium break-words px-1">
                              {colItem.item.name}
                            </h4>
                            <p className="text-sm text-neutral-600 dark:text-neutral-300 mt-0.5">
                              {colItem.item.strPrice}
                            </p>
                          </div>
                        </div>
                        <div className="text-xl text-neutral-600 dark:text-neutral-300 whitespace-nowrap">
                          X {colItem.amount}
                        </div>
                      </Link>)}
                  </div>
                </div>}
              {limits && renderLimits() && <div className="py-2 my-2">{renderLimits()}</div>}
              {amount !== undefined && location !== 'tr' && <div className="my-4">
                  <div className="mb-2 text-sm font-medium">
                    {amount === 0 ? t('text-out-of-stock') : amount > 20 ? t('text-in-stock-ready') : `${t('text-low-stock', {
                stock: amount
              })} !`}
                  </div>
                  {(() => {
              const progressMax = amount < 100 ? 200 : 1000;
              return <Progress value={Math.min(amount, progressMax) / progressMax * 100} max={progressMax} indicatorClassName={amount <= 10 ? 'bg-red-500' : amount > 20 ? 'bg-green-500' : 'bg-aloeGold'} />;
            })()}
                </div>}
              <hr className="my-4 border-t border-neutral-200 dark:border-neutral-800" />
            </div>}
          <div className="flex items-center gap-x-4 border-b border-neutral-300 dark:border-neutral-800 py-8">
            <Counter quantity={quantity} onIncrement={() => {
            const maxLimit = getMaxLimit(limits);
            if (maxLimit && quantity >= maxLimit) {
              setToastMessage(t('text-limit-reached'));
              setToastVariant('destructive');
              setToastOpen(true);
              start();
              return;
            }
            setQuantity(prev => prev + 1);
          }} onDecrement={() => setQuantity(prev => prev !== 1 ? prev - 1 : 1)} disableDecrement={quantity === 1} data-sentry-element="Counter" data-sentry-source-file="product-single-details.tsx" />
            <Button onClick={addToCart} variant="slim" className={`w-full md:w-6/12 xl:w-full ${!isSelected || amount === 0 ? 'bg-neutral-400 hover:bg-neutral-400' : ''}`} disabled={!isSelected || amount === 0} loading={addToCartLoader} data-sentry-element="Button" data-sentry-source-file="product-single-details.tsx">
              <span className="py-2 3xl:px-8">
                {amount === 0 ? t('text-out-of-stock') : t('text-add-to-cart')}
              </span>
            </Button>
          </div>
          <div className="py-6">
            <ul className="text-sm space-y-5 pb-1">
              <li className="flex items-center justify-between">
                {/* <div>
                  <span className="font-semibold text-heading dark:text-neutral-200 inline-block pr-2">
                    {t('text-category')}:
                  </span>
                  {categoryName ? (
                    <Link
                      href={`${ROUTES.SHOP}?category=${categoryId}`}
                      className="transition hover:underline hover:text-heading dark:hover:text-neutral-200"
                    >
                      {categoryName}
                    </Link>
                  ) : (
                    'Category not found'
                  )}
                 </div> */}
                <div className="flex items-center space-x-2 ml-auto">
                  <GlobalShare data-sentry-element="GlobalShare" data-sentry-source-file="product-single-details.tsx" />

                </div>
              </li>
            </ul>
          </div>
          <ProductMetaReview data={product} data-sentry-element="ProductMetaReview" data-sentry-source-file="product-single-details.tsx" />
        </div>
      </div>
      <AnimatePresence data-sentry-element="AnimatePresence" data-sentry-source-file="product-single-details.tsx">
        {isImageModalOpen && galleryImages.length > 0 && <ImageModal images={galleryImages} alt={name} onClose={() => setIsImageModalOpen(false)} />}
      </AnimatePresence>
      <Toast variant={toastVariant} open={toastOpen} onOpenChange={open => {
      setToastOpen(open);
      if (open) start();
    }} onPause={pause} onResume={resume} data-sentry-element="Toast" data-sentry-source-file="product-single-details.tsx">
        <div className="flex w-full justify-between gap-3">
          <CircleCheckIcon size={16} className={`mt-0.5 ${toastVariant === 'destructive' ? 'text-red-500' : 'text-emerald-500'}`} aria-hidden="true" data-sentry-element="CircleCheckIcon" data-sentry-source-file="product-single-details.tsx" />
          <div className="flex flex-col flex-grow gap-3">
            <ToastTitle data-sentry-element="ToastTitle" data-sentry-source-file="product-single-details.tsx">{toastMessage}</ToastTitle>
          </div>
          <ToastClose data-sentry-element="ToastClose" data-sentry-source-file="product-single-details.tsx">
            <button className="p-1">
              <XIcon size={16} aria-hidden="true" data-sentry-element="XIcon" data-sentry-source-file="product-single-details.tsx" />
            </button>
          </ToastClose>
        </div>
        <div className={`absolute -left-2 right-0 bottom-0 h-1 ${toastVariant === 'destructive' ? 'bg-red-500' : 'bg-emerald-500'}`} style={{
        width: `${progress / toastDuration * 100}%`,
        transition: 'width 100ms linear'
      }} />
      </Toast>
      <ToastViewport data-sentry-element="ToastViewport" data-sentry-source-file="product-single-details.tsx" />
    </ToastProvider>;
};
export default ProductSingleDetails;